import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Smartphone, Share2, Copy, Search, Loader2 } from "lucide-react";

export default function Promo() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/card-banners/slug/${slug}`)
      .then((r) => { setBanner(r.data); if (r.data?.id) api.post(`/card-banners/${r.data.id}/view`).catch(() => {}); })
      .catch(() => setBanner(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const url = window.location.href;
  const trackShare = () => { if (banner?.id) api.post(`/card-banners/${banner.id}/share`).catch(() => {}); };
  const shareText = banner ? `${banner.heading}${banner.description ? " — " + banner.description : ""}` : "My Property";
  const copyLink = () => { trackShare(); navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const waShare = () => { trackShare(); window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + url)}`, "_blank"); };
  const smsShare = () => { trackShare(); window.location.href = `sms:?body=${encodeURIComponent(shareText + " " + url)}`; };
  const genShare = async () => {
    trackShare();
    try {
      if (navigator.share) await navigator.share({ title: banner?.heading, text: banner?.description, url });
      else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch (e) { console.debug("share cancelled", e); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-emerald" /></div>;
  if (!banner) return <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="promo-notfound"><p className="text-brand-muted">This promotion is no longer available.</p><Button onClick={() => navigate("/browse")} className="rounded-full bg-brand-emerald">Browse Properties</Button></div>;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-12" data-testid="promo-page">
      <div className="max-w-lg w-full rounded-3xl overflow-hidden shadow-2xl" style={{ backgroundColor: banner.bg_color || "#1a5f5a" }}>
        {banner.image && <img src={fileUrl(banner.image)} alt={banner.heading} className="w-full h-52 object-cover" />}
        <div className="p-8 text-white text-center">
          <h1 className="font-heading font-bold text-3xl leading-tight" data-testid="promo-heading">{banner.heading}</h1>
          {banner.description && <p className="text-white/90 mt-3">{banner.description}</p>}
          {banner.cta_label && banner.cta_link && (
            <a href={banner.cta_link} target="_blank" rel="noopener noreferrer" className="block mt-5" data-testid="promo-cta">
              <Button className="rounded-full bg-white text-brand-ink hover:bg-white/90 font-semibold">{banner.cta_label}</Button>
            </a>
          )}
          <Button onClick={() => navigate("/browse")} className="mt-6 rounded-full bg-white text-brand-ink hover:bg-white/90" data-testid="promo-browse">
            <Search className="w-4 h-4 mr-1.5" /> Browse Properties
          </Button>
          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="text-white/70 text-xs uppercase tracking-widest mb-3">Share this</p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button onClick={waShare} size="sm" variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="promo-share-whatsapp"><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button>
              <Button onClick={smsShare} size="sm" variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="promo-share-sms"><Smartphone className="w-4 h-4 mr-1" />SMS</Button>
              <Button onClick={genShare} size="sm" variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="promo-share-general"><Share2 className="w-4 h-4 mr-1" />Share</Button>
              <Button onClick={copyLink} size="sm" variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="promo-copy"><Copy className="w-4 h-4 mr-1" />Copy</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
